import { useIsDesktop } from '@/hooks/use-screen'
import Autoplay from 'embla-carousel-autoplay'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import ExportedImage from 'next-image-export-optimizer'
import { Fragment, useEffect, useState } from 'react'

import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/components/atoms/ui/carousel'

import { cn } from '@/lib/utils'

interface OurServiceProps {
  data: Klien
  variant?: 'default' | 'secondary'
}

export const OurClientCarousel = ({ data, variant = 'default' }: OurServiceProps) => {
  const isDesktop = useIsDesktop()
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    if (!api) return

    setCurrent(api.selectedScrollSnap() + 1)

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1)
    })
  }, [api])

  const getImage = (id?: string) => {
    return data.includes.Asset.find((item) => item.sys.id === id)
  }

  const getCarouselItem = (item: { sys: { id: string } }) => {
    const image = getImage(item.sys.id)

    return (
      <div key={item.sys.id}>
        <ExportedImage
          src={image?.fields.file.url ? 'https:' + image?.fields.file.url : '/static/images/sawala-logo.png'}
          alt={image?.fields.title + 'Logo'}
          width={108}
          height={108}
          sizes="108px"
          className="object-contain mx-auto size-[108px]"
        />
      </div>
    )
  }

  const chunkArray = (arr: Klien['items'][0]['fields']['partner'], chunkSize: number) => {
    const result = []
    for (let i = 0; i < arr.length; i += chunkSize) {
      result.push(arr.slice(i, i + chunkSize))
    }

    return result
  }

  const getMobileSlides = () => {
    const items = data?.items[0].fields.partner
    const slides = chunkArray(items, 4)

    return slides
  }

  const itemsLength = data.items[0].fields.partner.length

  return (
    <Carousel
      id="top"
      opts={{
        align: 'start',
        slidesToScroll: isDesktop ? 8 : 1,
        duration: isDesktop ? 70 : 40,
        loop: true
      }}
      plugins={[
        Autoplay({
          delay: 7000
        })
      ]}
      setApi={setApi}
      className="relative px-4 space-y-8 lg:px-0"
    >
      {isDesktop && (
        <CarouselContent className="sm:-ml-7">
          {data?.items[0].fields.partner.slice(0, 24).map((item, index) => (
            <CarouselItem key={index} className={cn('sm:pl-7 basis-1/2 sm:basis-1/4 lg:basis-[12.5%]')}>
              {getCarouselItem(item)}
            </CarouselItem>
          ))}
        </CarouselContent>
      )}

      {!isDesktop && (
        <CarouselContent className="-ml-2">
          {getMobileSlides().map((slide, index) => (
            <CarouselItem key={index} className="flex flex-col gap-4 basis-full">
              <div className="flex gap-4">
                {slide.slice(0, 2).map((item, subIndex) => (
                  <div key={subIndex} className="basis-1/2">
                    {getCarouselItem(item)}
                  </div>
                ))}
              </div>
              <div className="flex gap-4">
                {slide.slice(2, 4).map((item, subIndex) => (
                  <div key={subIndex} className="basis-1/2">
                    {getCarouselItem(item)}
                  </div>
                ))}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      )}

      {variant === 'default' && (
        <Fragment>
          <div className="flex justify-center">
            <div className="flex w-auto gap-2">
              {data.items[0].fields.partner
                .slice(0, isDesktop ? Math.ceil(itemsLength / 8) : Math.ceil(itemsLength / 4))
                .map((_: unknown, index: number) => (
                  <div
                    className={cn(
                      'h-3 w-3 rounded-full transition-all ease-in-out duration-500',
                      index + 1 === current ? 'bg-primary-blue' : 'bg-slate-300'
                    )}
                    key={index}
                  />
                ))}
            </div>
          </div>

          <div>
            <CarouselPrevious
              className="absolute z-10 flex items-center justify-center w-10 h-10 lg:translate-y-[-50%] bg-transparent border border-gray-300 rounded-full text-primary-blue bg-white hover:text-white disabled:text-primary-blue/60 hover:bg-primary-blue top-[37%] -left-2 lg:-left-10 group"
              customArrow={<ChevronLeftIcon className="w-5 h-5" />}
            />
            <CarouselNext
              className="absolute z-10 flex items-center justify-center w-10 h-10 lg:translate-y-[-50%] bg-transparent border border-gray-300 rounded-full text-primary-blue bg-white hover:text-white disabled:text-primary-blue/60 hover:bg-primary-blue top-[37%] -right-2 lg:-right-10 group"
              customArrow={<ChevronRightIcon className="w-5 h-5" />}
            />
          </div>
        </Fragment>
      )}

      {variant === 'secondary' && (
        <div className="flex items-center justify-center gap-6">
          <CarouselPrevious
            className="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-300 text-primary-blue disabled:text-primary-blue/60 group"
            customArrow={<ChevronLeftIcon className="w-5 h-5" />}
          />
          <div className="flex w-auto gap-2">
            {data.items[0].fields.partner
              .slice(0, isDesktop ? Math.ceil(itemsLength / 8) : Math.ceil(itemsLength / 4))
              .map((_: unknown, index: number) => (
                <div
                  className={cn(
                    'h-3 w-3 rounded-full transition-all ease-in-out duration-500',
                    index + 1 === current ? 'bg-primary-blue' : 'bg-slate-300'
                  )}
                  key={index}
                />
              ))}
          </div>
          <CarouselNext
            className="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-300 text-primary-blue disabled:text-primary-blue/60 group"
            customArrow={<ChevronRightIcon className="w-5 h-5" />}
          />
        </div>
      )}
    </Carousel>
  )
}
